/**
 * sl-oll9.10: useElsterSchemaMigration — Thin wrapper around useSchemaMigration.
 *
 * Provides ELSTER-specific configuration:
 * - REST endpoints for ELSTER schema API
 * - mapSchemaToColumns: UstvaSchema → ColumnDefinition[] (Kz-based)
 * - extractFieldId: column name → "KzXX" or special field ID
 *
 * SOLID:
 * - S: Only ELSTER-specific config for the generic hook
 * - O: New ELSTER fields = only schema changes, no code changes here
 */

import { type DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import { useCallback, useMemo } from 'react';

import type { ColumnDefinition, DbColumnType } from './schema-diff';
import type { MigrationState } from './use-schema-migration';
import { useSchemaMigration } from './use-schema-migration';
import { extractKzFromColumnName } from './ustva-constants';

// ─── ELSTER Types ────────────────────────────────────────────────────────────

export interface UstvaFieldDef {
  name: string;
  type: 'integer' | 'decimal' | 'flag' | 'date' | 'enum' | 'string' | 'year';
  signed: boolean;
  required: boolean;
  label: string;
  xsdType: string;
  maxDigits?: number;
  exactDecimalPlaces?: number;
  decimalSeparator?: string;
  pattern?: string;
  enumValues?: Record<string, string>;
  minLength?: number;
  maxLength?: number;
}

export interface UstvaSchema {
  version: string;
  source: string;
  sourceHash: string;
  generatedAt: string;
  generator: string;
  fields: Record<string, UstvaFieldDef>;
  metadata: Record<string, UstvaFieldDef>;
  sections: string[];
}

// ─── ELSTER-specific mapping ─────────────────────────────────────────────────

/** Map XSD field type to BlockSuite database column type */
function mapFieldTypeToDbType(fieldType: UstvaFieldDef['type']): DbColumnType {
  switch (fieldType) {
    case 'integer':
    case 'decimal':
      return 'number';
    case 'flag':
      return 'checkbox';
    case 'date':
      return 'date';
    case 'enum':
      return 'select';
    case 'string':
    case 'year':
      return 'rich-text';
  }
}

/** Build column name matching provisioning pattern: "Label (KzXX)" */
function buildColumnName(kz: string, field: UstvaFieldDef): string {
  const label =
    field.label.length > 40 ? field.label.slice(0, 37) + '...' : field.label;
  return `${label} (${kz})`;
}

/** Convert UstvaSchema fields to generic ColumnDefinitions */
function mapUstvaSchemaToColumns(schema: UstvaSchema): ColumnDefinition[] {
  return Object.entries(schema.fields)
    .filter(([kz]) => kz !== 'Kz500_Begruendung') // Dependent text field, not standalone
    .map(([kz, field]) => ({
      id: kz,
      name: buildColumnName(kz, field),
      dbType: mapFieldTypeToDbType(field.type),
      label: field.label,
      enumValues: field.enumValues,
    }));
}

/** Extract field ID from column name: "Label (KzXX)" → "KzXX" or "(FieldName)" → "FieldName" */
function extractElsterFieldId(columnName: string): string | null {
  // Regular Kz fields: extract number and prefix with "Kz"
  const kzNum = extractKzFromColumnName(columnName);
  if (kzNum !== null) return `Kz${kzNum}`;

  // Special fields: extract from parens "(WIdNr)", "(Kz500_Begruendung)"
  const match = /\(([A-Za-z]\w+)\)/.exec(columnName);
  return match ? match[1] : null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * ELSTER-specific schema migration hook.
 * Thin wrapper around useSchemaMigration with ELSTER config.
 */
export function useElsterSchemaMigration(
  workspaceId: string,
  schemaVersion: string | null,
  inputDataSource: DatabaseBlockDataSource | null
): MigrationState<UstvaSchema> {
  const mapSchemaToColumns = useCallback(mapUstvaSchemaToColumns, []);
  const extractFieldId = useCallback(extractElsterFieldId, []);
  const getVersionString = useCallback(
    (schema: UstvaSchema) =>
      `${schema.version}-${schema.sourceHash.slice(0, 12)}`,
    []
  );

  const config = useMemo(
    () => ({
      workspaceId,
      versionsEndpoint: `/api/workspace/${workspaceId}/elster/schema/versions`,
      schemaEndpoint: `/api/workspace/${workspaceId}/elster/schema`,
      canUpdateEndpoint: `/api/workspace/${workspaceId}/elster/schema/can-update`,
      dataSource: inputDataSource,
      currentVersion: schemaVersion,
      mapSchemaToColumns,
      extractFieldId,
      getVersionString,
    }),
    [
      workspaceId,
      inputDataSource,
      schemaVersion,
      mapSchemaToColumns,
      extractFieldId,
      getVersionString,
    ]
  );

  return useSchemaMigration<UstvaSchema>(config);
}
